/**
 * Shoulder Anatomy Page
 * SEO-optimized page targeting "scapula", "arm bones names", "clavicle bone", "shoulder anatomy"
 * Educational content with internal links to injury/condition pages
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { CTASection } from '../components/CTASection';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Bone, Activity, BookOpen } from 'lucide-react';
import { useSite } from '../contexts/SiteContext';

export const ShoulderAnatomy = () => {
  const { theme, doctor } = useSite();

  const bones = [
    {
      name: "Scapula (Shoulder Blade)",
      description: "A flat, triangular bone that forms the back of the shoulder. It provides attachment for 17 muscles and is crucial for shoulder movement.",
      key_parts: ["Glenoid (socket)", "Acromion", "Coracoid process", "Scapular spine"],
      related_injuries: [
        { name: "Scapula Fracture", link: "/scapula-fracture" },
        { name: "AC Joint Injury", link: "/ac-joint-injury" }
      ]
    },
    {
      name: "Clavicle (Collarbone)",
      description: "An S-shaped bone connecting the shoulder to the sternum (breastbone). It's the most commonly fractured bone in the body.",
      key_parts: ["Medial end (sternum)", "Lateral end (acromion)", "S-shaped curve"],
      related_injuries: [
        { name: "Clavicle Fracture", link: "/clavicle-fracture" },
        { name: "AC Joint Injury", link: "/ac-joint-injury" },
        { name: "SC Joint Injury", link: "/sc-joint-injury" }
      ]
    },
    {
      name: "Humerus (Upper Arm Bone)",
      description: "The long bone of the upper arm. The rounded head articulates with the glenoid to form the main shoulder joint.",
      key_parts: ["Humeral head (ball)", "Greater tuberosity", "Lesser tuberosity", "Bicipital groove"],
      related_injuries: [
        { name: "Shoulder Fracture", link: "/shoulder-fracture" },
        { name: "Shoulder Dislocation", link: "/shoulder-dislocation" },
        { name: "Shoulder Replacement", link: "/shoulder-replacement" }
      ]
    }
  ];

  const joints = [
    {
      name: "Glenohumeral Joint (GH Joint)",
      type: "Ball-and-socket joint",
      description: "The main shoulder joint where the humeral head (ball) meets the glenoid (socket). Provides the greatest range of motion of any joint in the body.",
      structures: ["Labrum", "Capsule", "Glenohumeral ligaments", "Rotator cuff"],
      conditions: [
        { name: "Shoulder Dislocation", link: "/shoulder-dislocation" },
        { name: "Labral Tears", link: "/labral-tears" },
        { name: "Shoulder Arthritis", link: "/shoulder-arthritis" },
        { name: "Frozen Shoulder", link: "/frozen-shoulder" }
      ]
    },
    {
      name: "Acromioclavicular Joint (AC Joint)",
      type: "Plane/gliding joint",
      description: "Where the clavicle meets the acromion of the scapula. Commonly injured in falls onto the shoulder tip.",
      structures: ["AC ligament", "Coracoclavicular ligaments", "Articular disc"],
      conditions: [
        { name: "AC Joint Injury (Shoulder Separation)", link: "/ac-joint-injury" }
      ]
    },
    {
      name: "Sternoclavicular Joint (SC Joint)",
      type: "Saddle joint",
      description: "Where the clavicle meets the sternum. The only true bony connection between the arm and the trunk.",
      structures: ["Costoclavicular ligament", "Interclavicular ligament", "Articular disc"],
      conditions: [
        { name: "SC Joint Injury", link: "/sc-joint-injury" }
      ]
    },
    {
      name: "Scapulothoracic Articulation",
      type: "Functional joint (not a true joint)",
      description: "The gliding interface between the scapula and the ribcage. Essential for normal shoulder movement.",
      structures: ["Serratus anterior", "Rhomboids", "Trapezius", "Levator scapulae"],
      conditions: [
        { name: "Scapular Dyskinesis", link: "/shoulder-pain" }
      ]
    }
  ];

  const rotatorCuffMuscles = [
    {
      name: "Supraspinatus",
      function: "Initiates arm abduction (raising arm away from body). Most commonly injured rotator cuff muscle.",
      injury_link: "/rotator-cuff-tear"
    },
    {
      name: "Infraspinatus",
      function: "External rotation (rotating arm outward). Second most commonly torn.",
      injury_link: "/rotator-cuff-tear"
    },
    {
      name: "Teres Minor",
      function: "External rotation and stabilization. Works with infraspinatus.",
      injury_link: "/rotator-cuff-tear"
    },
    {
      name: "Subscapularis",
      function: "Internal rotation (rotating arm inward). Largest rotator cuff muscle.",
      injury_link: "/rotator-cuff-tear"
    }
  ];

  return (
    <div className="min-h-screen" data-testid="shoulder-anatomy-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: `${theme?.primaryHex}20`, color: theme?.primaryHex }}>
              Educational Resource
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Shoulder Anatomy: Complete Guide
            </h1>
            <p className="text-xl text-gray-600">
              Understanding the bones, joints, muscles, and structures of the shoulder complex. 
              By Dr. Deepthi Nandan Reddy, Shoulder Specialist, Hyderabad.
            </p>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding the Shoulder</h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                The shoulder is the most mobile joint in the human body, allowing an incredible range of movement in multiple directions. 
                This mobility comes from a complex arrangement of bones, joints, muscles, ligaments, and tendons working together.
              </p>
              <p>
                The shoulder complex consists of <strong>3 bones</strong> (scapula, clavicle, humerus), <strong>4 joints</strong> 
                (glenohumeral, acromioclavicular, sternoclavicular, and scapulothoracic), and over <strong>20 muscles</strong> that 
                provide movement and stability.
              </p>
              <p>
                Understanding shoulder anatomy helps you better understand your injury, communicate with your doctor, and participate 
                in your rehabilitation. Click on any structure below to learn about related injuries and conditions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bones Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Bone className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Bones of the Shoulder</h2>
            </div>
            
            <div className="space-y-6">
              {bones.map((bone, index) => (
                <Card key={index} className="border-l-4" style={{ borderLeftColor: theme?.primaryHex }}>
                  <CardHeader>
                    <CardTitle className="text-xl">{bone.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 mb-4">{bone.description}</p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="font-semibold text-gray-900 mb-2">Key Parts:</p>
                        <ul className="text-gray-600 text-sm space-y-1">
                          {bone.key_parts.map((part, idx) => (
                            <li key={idx}>• {part}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-2">Related Injuries:</p>
                        <div className="space-y-1">
                          {bone.related_injuries.map((injury, idx) => (
                            <Link 
                              key={idx}
                              to={injury.link}
                              className="flex items-center gap-2 text-sm py-1 hover:underline"
                              style={{ color: theme?.primaryHex }}
                            >
                              {injury.name}
                              <ArrowRight className="w-3 h-3" />
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Joints Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Activity className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Joints of the Shoulder</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {joints.map((joint, index) => (
                <Card key={index}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{joint.name}</CardTitle>
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">{joint.type}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 text-sm mb-3">{joint.description}</p>
                    
                    <p className="font-semibold text-gray-900 text-sm mb-1">Key Structures:</p>
                    <p className="text-gray-600 text-xs mb-3">{joint.structures.join(", ")}</p>
                    
                    <p className="font-semibold text-gray-900 text-sm mb-1">Related Conditions:</p>
                    <div className="flex flex-wrap gap-2">
                      {joint.conditions.map((condition, idx) => (
                        <Link 
                          key={idx}
                          to={condition.link}
                          className="text-xs px-2 py-1 rounded hover:opacity-80 transition-opacity"
                          style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}
                        >
                          {condition.name}
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Rotator Cuff Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">The Rotator Cuff</h2>
            <p className="text-gray-700 mb-8">
              The rotator cuff is a group of four muscles and their tendons that surround and stabilize the shoulder joint. 
              They work together to keep the humeral head centered in the glenoid socket during movement. 
              <strong> Remember the acronym SITS: Supraspinatus, Infraspinatus, Teres minor, Subscapularis.</strong>
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {rotatorCuffMuscles.map((muscle, index) => (
                <Card key={index} className="border-t-4" style={{ borderTopColor: theme?.primaryHex }}>
                  <CardHeader>
                    <CardTitle className="text-lg">{muscle.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 text-sm mb-3">{muscle.function}</p>
                    <Link 
                      to={muscle.injury_link}
                      className="inline-flex items-center gap-1 text-sm hover:underline"
                      style={{ color: theme?.primaryHex }}
                    >
                      Learn about Rotator Cuff Tears
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <div className="mt-8 p-6 bg-orange-50 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">Did You Know?</h3>
              <p className="text-gray-700">
                The rotator cuff muscles generate 50% of shoulder strength for elevation and 90% for external rotation. 
                When these muscles are torn, the shoulder loses both strength and stability, leading to pain and difficulty 
                with overhead activities.
              </p>
              <Link 
                to="/rotator-cuff-tear"
                className="inline-flex items-center gap-2 mt-4 font-medium"
                style={{ color: theme?.primaryHex }}
              >
                Read more about Rotator Cuff Tear Treatment in Hyderabad
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Other Important Structures */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Other Important Structures</h2>
            
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Labrum (Glenoid Labrum)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-3">
                    A ring of fibrocartilage that surrounds the glenoid socket, deepening it by 50% and providing stability. 
                    The labrum also serves as the attachment site for the biceps tendon (superiorly) and glenohumeral ligaments.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Link to="/labral-tears" className="text-sm px-3 py-1 rounded hover:opacity-80" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>Labral Tears</Link>
                    <Link to="/slap-tears" className="text-sm px-3 py-1 rounded hover:opacity-80" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>SLAP Tears</Link>
                    <Link to="/shoulder-dislocation" className="text-sm px-3 py-1 rounded hover:opacity-80" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>Bankart Lesion</Link>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Biceps Tendon (Long Head)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-3">
                    The long head of the biceps tendon attaches to the top of the glenoid (superior labrum) and travels through 
                    the shoulder joint before passing through the bicipital groove of the humerus. It's a common source of 
                    shoulder pain and is often injured along with the rotator cuff.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Link to="/biceps-tendon-injury" className="text-sm px-3 py-1 rounded hover:opacity-80" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>Biceps Tendon Injury</Link>
                    <Link to="/slap-tears" className="text-sm px-3 py-1 rounded hover:opacity-80" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>SLAP Tears</Link>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Subacromial Bursa</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-3">
                    A fluid-filled sac between the rotator cuff and the acromion that reduces friction during movement. 
                    When inflamed (subacromial bursitis), it contributes to impingement syndrome and shoulder pain.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Link to="/shoulder-impingement" className="text-sm px-3 py-1 rounded hover:opacity-80" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>Shoulder Impingement</Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Reference */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <BookOpen className="w-6 h-6" style={{ color: theme?.primaryHex }} />
              <h3 className="text-xl font-bold text-gray-700">Quick Reference: Common Search Terms</h3>
            </div>
            <div className="bg-white p-6 rounded-lg">
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="font-semibold text-gray-900 mb-2">Bone Names:</p>
                  <ul className="text-gray-600 space-y-1">
                    <li>• Scapula = Shoulder blade</li>
                    <li>• Clavicle = Collarbone</li>
                    <li>• Humerus = Upper arm bone</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-2">Joint Names:</p>
                  <ul className="text-gray-600 space-y-1">
                    <li>• GH Joint = Main shoulder joint</li>
                    <li>• AC Joint = Top of shoulder</li>
                    <li>• SC Joint = Collarbone to chest</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-2">Structure Names:</p>
                  <ul className="text-gray-600 space-y-1">
                    <li>• Acromion = Bony roof of shoulder</li>
                    <li>• Glenoid = Socket of shoulder</li>
                    <li>• Labrum = Cartilage rim</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default ShoulderAnatomy;
